import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";
import { RefreshCw, AlertCircle } from "lucide-react";
import { StatusPill, Modal, StatsRow } from "@/components/phase4/Phase4Common";

export default function DemurrageQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");
  const [stats, setStats] = useState({ calc: 0, billed: 0, waived: 0, total: 0 });
  const [billing, setBilling] = useState(null);
  const [waiving, setWaiving] = useState(null);
  const [waiveReason, setWaiveReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, page_size: 100 };
      if (filterStatus) params.status = filterStatus;
      const r = await api.get("/v2/demurrage", { params });
      let list = r.data?.items || [];
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        list = list.filter((d) =>
          (d.awb || "").toLowerCase().includes(q) ||
          (d.demurrage_id || "").toLowerCase().includes(q) ||
          (d.customer_id || "").toLowerCase().includes(q)
        );
      }
      setItems(list);

      const all = await api.get("/v2/demurrage", { params: { page_size: 1000 } });
      const allItems = all.data?.items || [];
      setStats({
        calc: allItems.filter((d) => d.status === "CALCULATED").length,
        billed: allItems.filter((d) => d.status === "BILLED").length,
        waived: allItems.filter((d) => d.status === "WAIVED").length,
        total: allItems.filter((d) => d.status === "BILLED").reduce((s, d) => s + (d.calculated_amount || 0), 0),
      });
    } catch (e) { toast.error("Failed to load"); }
    setLoading(false);
  }, [filterStatus, search]);

  useEffect(() => { load(); }, [load]);

  const confirmBill = async () => {
    if (!billing) return;
    setBusy(true);
    try {
      await api.post(`/v2/demurrage/bill/${billing.demurrage_id}`, {});
      toast.success(`Billed ₹${billing.calculated_amount.toFixed(2)}`);
      setBilling(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const confirmWaive = async () => {
    if (!waiving || !waiveReason.trim()) { toast.error("Reason required"); return; }
    setBusy(true);
    try {
      await api.put(`/v2/demurrage/waive/${waiving.demurrage_id}`, { reason: waiveReason });
      toast.success("Demurrage waived");
      setWaiving(null); setWaiveReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="demurrage-queue">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Demurrage Queue</h1>
            <p className="text-slate-500 text-sm mt-0.5">Manage storage and re-delivery charges</p>
          </div>
          <button onClick={load} className="px-3 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded inline-flex items-center gap-1">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        <StatsRow items={[
          { key: "calc", label: "Calculated", value: stats.calc, color: "text-amber-600" },
          { key: "billed", label: "Billed", value: stats.billed, color: "text-blue-600" },
          { key: "waived", label: "Waived", value: stats.waived, color: "text-slate-600" },
          { key: "total", label: "Total ₹ Billed", value: `₹${stats.total.toFixed(0)}`, color: "text-emerald-600" },
        ]} />

        <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap gap-2 items-end">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="demurrage-filter-status"
            className="text-xs border border-slate-200 rounded px-2 py-1.5">
            <option value="">All Statuses</option>
            {["CALCULATED", "BILLED", "WAIVED", "PAID"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="demurrage-search"
            placeholder="Search AWB / DMR ID…"
            className="flex-1 text-xs border border-slate-200 rounded px-2 py-1.5" />
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="demurrage-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["DMR ID", "AWB", "Customer", "Attempts", "Days Overdue", "Calculated ₹", "Status", "Actions"].map((h) =>
                  <th key={h} className="text-left px-3 py-2 text-[10px] font-bold text-slate-500 uppercase">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? <tr><td colSpan={8} className="text-center py-8 text-slate-400">Loading…</td></tr>
                : items.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-slate-400">No demurrage records.</td></tr>
                : items.map((d) => (
                  <tr key={d.demurrage_id} className="hover:bg-slate-50" data-testid={`demurrage-row-${d.demurrage_id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{d.demurrage_id}</td>
                    <td className="px-3 py-2 font-mono text-xs">{d.awb || "—"}</td>
                    <td className="px-3 py-2 text-xs">{d.customer_id?.slice(-6) || "—"}</td>
                    <td className="px-3 py-2 text-xs text-center">{d.actual_attempts}/{d.free_attempts}</td>
                    <td className="px-3 py-2 text-xs text-center">{d.days_overdue}</td>
                    <td className="px-3 py-2 text-xs font-semibold relative group">
                      <span className="cursor-help" data-testid={`demurrage-amount-${d.demurrage_id}`}>
                        ₹{(d.calculated_amount || 0).toFixed(2)}
                      </span>
                      <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block bg-slate-900 text-white text-[10px] rounded p-2 z-10 w-64 shadow-lg">
                        <div>Flat minimum: ₹{(d.components?.flat_minimum ?? 500).toFixed(2)}</div>
                        <div>LM × 5: ₹{(d.components?.lm_multiplier_x_lm ?? 0).toFixed(2)}</div>
                        <div>Per kg/day: ₹{(d.components?.per_kg_per_day_x_kg_x_days ?? 0).toFixed(2)}</div>
                        <div className="mt-1 pt-1 border-t border-slate-700 font-bold">
                          Applied: ₹{(d.calculated_amount || 0).toFixed(2)} (highest)
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2"><StatusPill status={d.status} /></td>
                    <td className="px-3 py-2">
                      {d.status === "CALCULATED" && (
                        <div className="flex gap-1">
                          <button onClick={() => setBilling(d)}
                            data-testid={`demurrage-bill-${d.demurrage_id}`}
                            className="px-2 py-1 bg-brand-500 hover:bg-brand-700 text-white text-[10px] font-semibold rounded">Bill Customer</button>
                          <button onClick={() => setWaiving(d)}
                            data-testid={`demurrage-waive-${d.demurrage_id}`}
                            className="px-2 py-1 bg-slate-200 hover:bg-slate-300 text-slate-700 text-[10px] font-semibold rounded">Waive</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={!!billing} onClose={() => setBilling(null)} title="Confirm Bill" testId="demurrage-bill-modal">
        {billing && (
          <>
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs flex gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                ₹{(billing.calculated_amount || 0).toFixed(2)} will be deducted from customer wallet ({billing.customer_id?.slice(-8) || "—"}). Confirm?
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setBilling(null)} className="px-3 py-1.5 border border-slate-200 text-xs rounded">Cancel</button>
              <button onClick={confirmBill} disabled={busy}
                data-testid="demurrage-bill-confirm"
                className="px-3 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded disabled:opacity-50">Confirm Bill</button>
            </div>
          </>
        )}
      </Modal>

      <Modal open={!!waiving} onClose={() => setWaiving(null)} title="Waive Demurrage" testId="demurrage-waive-modal">
        <textarea value={waiveReason} onChange={(e) => setWaiveReason(e.target.value)} rows={3}
          data-testid="demurrage-waive-reason"
          placeholder="Reason for waiving (required)"
          className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={() => { setWaiving(null); setWaiveReason(""); }} className="px-3 py-1.5 border border-slate-200 text-xs rounded">Cancel</button>
          <button onClick={confirmWaive} disabled={busy || !waiveReason.trim()}
            data-testid="demurrage-waive-confirm"
            className="px-3 py-1.5 bg-slate-700 text-white text-xs font-semibold rounded disabled:opacity-50">Waive</button>
        </div>
      </Modal>
    </DashboardLayout>
  );
}
